import React, { useEffect, useRef, useState } from 'react';
import c from 'classnames';
import styles from './VideoPlayer.css';
import { settings } from '../../settings';
import Script from 'react-load-script';
import { useSelector } from 'react-redux';
import {
  selectDidomiConsentAccepted,
  selectKachingInitialized,
  selectKachingInstance,
  selectShowNsfw,
  selectUpNext,
} from '../../ducks';
import nsfwCardStaticImg from './nsfw-card-static.jpg';
import { useLocation } from 'react-router-dom';

let agnoplayPatched = false;

export const agnoplayPatch = () => {
  if(!agnoplayPatched) {
    agnoplayPatched = true;
    document.addEventListener('agnoLoaded', e => {
      const playerId = e.detail.playerId;
      const player = window.AGNO.Players.getPlayerById(playerId).agnoInstance;
      player.addEventListener('playing', () => {
        window.AGNO.pauseAllExcept(playerId);
      });
    });
  }
};

export const VideoPlayer = ({ itemId, variants, still, title, description, duration, dateCreated, tags, 
  related = [], index = 0, embedded, noads }) => {
  const { search } = useLocation();
  const ref = useRef({ current: null });
  const [scriptsLoaded, setScriptsLoaded] = useState(0);
  const [scriptsErrors, setScriptsErrors] = useState(0);
  const [player, setPlayer] = useState(null);
  const upnext = useSelector(selectUpNext);
  const showNsfw = useSelector(selectShowNsfw);
  const kaching = useSelector(selectKachingInstance);
  const kachingInitialized = useSelector(selectKachingInitialized);
  const didomiConsentAccepted = useSelector(selectDidomiConsentAccepted);
  const dateFormat = dateCreated ? dateCreated.slice(0, 19).split('T') : ['-', '-'];
  const mediaId = variants[0].uri.match(/[0-9a-z-]{36}/) ? variants[0].uri.match(/[0-9a-z-]{36}/)[0] : '';
  const onScriptLoad = () => setScriptsLoaded(x => x + 1);
  const onScriptError = () => setScriptsErrors(x => x + 1);

  const widthForVariant = (version) => {
    switch(version) {
    case '720p': return 720;
    case '1080p': return 1080;
    case 'tablet': return 480;
    case 'mobile': return 270;
    default: return 480;
    }
  };

  if (scriptsLoaded + scriptsErrors > 1 && !player && (kachingInitialized || noads || embedded)) {
    const config = {
      kaching: noads ? null : kaching,
      noads: noads || (!didomiConsentAccepted && kachingInitialized),
      upnext,
      autoplay: !embedded && index === 0,
      brand: 'dumpert',
      videoId: mediaId,
      url: window.location.href,
      socialUrl: window.location.href,
      recommendations: true,
      personalized_recommendations: true,
      premium_text: 'PREMIUM',
      related: embedded ? undefined : related.filter(item => !item.nsfw).slice(0, 9).map(item => ({
        id: item.id,
        image: item.nsfw && !showNsfw ? nsfwCardStaticImg : item.stills['still-medium'],
        link: `/item/${item.id}`,
        extra: false,
        title: item.title,
      })),
      video_metadata: {
        duration,
        title,
        description,
        date_created: dateFormat[0]+' '+dateFormat[1],
        format: 'Dumpert',
        tags: tags ? tags.split(' ').join(',') : '',
        category: 'Dumpert',
        publisher: 'Dumpert',
      },
    };
    if(!noads) {
      config.has_video_object = true;
      config.video_metadata.upload_date = dateFormat[0]+' '+dateFormat[1];
    }
    const stream = variants.find(x => x.version === 'stream');
    const liveStream = variants.find(x => x.version === 'agnoplay');
    if(liveStream) {

      // special item of live streaming from dupmert studio
      config.brand = 'dumpert-live';
      config.videoId = 'tmg2';
    } else if (stream) {

      config.stream_source = 'custom';
      config.custom_source = {
        source: stream.uri,
        thumbnail: still,
        title,
      };
    } else {
      config.stream_source = 'custom';
      config.custom_source = {
        title,
        thumbnail: still,
        source: variants.map(v => ({ width: widthForVariant(v.version), src: v.uri })),
      };
    }
    if(search) {
      try {
        const token = new URLSearchParams(search).get('access_token');
        if (token) {
          config.vhsSourceRequestHeaders = { 'Authorization': `Bearer ${token}`};
        }
      } catch (e) {

        /* no op */
      }

    }
    setPlayer(window.AGNO.insertPlayer(config, ref.current));
  }

  useEffect(() => () => {
    if (player) {
      try {
        window.AGNO.disposePlayer(player);
      } catch (e) { /* no-op */ }

      // remove tags added by AGNO player
      [].filter.call(
        document.getElementsByTagName('script'),
        x => x.type === 'application/ld+json' && x.id && x.id.startsWith('videoObject-'),
      ).forEach(x => x.remove());
    }

  }, [player]);

  useEffect(agnoplayPatch, []);

  return (
    <>
      <Script url={`${settings.PLAYER_SCRIPT_1}`} onLoad={onScriptLoad} onError={onScriptError}/>
      <Script url={`${settings.PLAYER_SCRIPT_2}`} onLoad={onScriptLoad} onError={onScriptError}/>
      {scriptsErrors > 1 && <span>{`${scriptsErrors} errors player script is not loaded`}</span>}
      <div
        ref={ref}
        className={c(
          styles.player,
          { [styles.embed]: embedded },
        )}
        id={index > 0 ? `${itemId}-${index}` : itemId}>
      </div>
    </>
  );
};
